import {
  useSearchInput,
  useTranslations,
  useAppVariant,
  assignRefs,
  cx,
} from "@jugl-web/utils";
import React, { useState, useMemo, ReactNode } from "react";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useOnboarding } from "@web-src/modules/preferences/providers/useOnboarding";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getPendingInvoiceType } from "@jugl-web/rest-api";
import {
  Button,
  Tooltip,
  SpotlightTooltip,
  Menu,
  ExpandableSearchBar,
} from "@jugl-web/ui-kit";
import { ReactComponent as AddIcon } from "./assets/add-icon.svg";
import { ReactComponent as BinIcon } from "./assets/bin.svg";
import { useCPanelPageContext } from "../../useCPanelPageContext";
import { DeleteUsersConfirmation } from "../DeleteUsersConfirmation";
import { ReactComponent as AddMemberIcon } from "./assets/add-member.svg";
import { ReactComponent as CopyLinkIcon } from "./assets/copy-link.svg";
import { ReactComponent as PeopleIcon } from "./assets/people-icon.svg";
import { OutOfSeatsDialog } from "./components/OutOfSeatsDialog";

export const PageControlBar: React.FC = () => {
  const {
    setSearchQuery,
    selectedUsers,
    clearSelectedUsers,
    setOpenedSideBar,
    setOpenedAlert,
    totalUsersCount,
    isOutOfSeatsDialogOpen,
    setIsOutOfSeatsDialogOpen,
  } = useCPanelPageContext();
  const { openSpotlight, openedSpotlight } = useOnboarding();
  const { inputProps } = useSearchInput();
  const { subscriptionInfo } = useEntityProvider();
  const { isCx } = useAppVariant();
  const {
    userCount,
    extraUserCount,
    isFree,
    isSubscriptionCanceled,
    pendingInvoices,
    isShopifyBilling,
  } = useSubscriptionV2();
  const { t } = useTranslations();
  const [
    isOpenDeleteUsersConfirmationPopup,
    setIsOpenDeleteUsersConfirmationPopup,
  ] = useState(false);
  const hasUserAddonPendingInvoice =
    isCx &&
    pendingInvoices.some((i) => getPendingInvoiceType(i) === "addon-seats") &&
    totalUsersCount >= userCount - extraUserCount;

  // For CX: use V2 entitlements for licensed seats, CPanelTable total for utilized
  // For org: use V1 subscriptionInfo as before
  const seatData = useMemo(() => {
    if (isCx) {
      const licensed = userCount;
      const utilized = totalUsersCount;
      return {
        licensed,
        utilized,
        available: Math.max(0, licensed - utilized),
        hasData: licensed > 0,
      };
    }
    if (!subscriptionInfo) return null;
    return {
      licensed: subscriptionInfo.seatsLicensed || 0,
      utilized:
        (subscriptionInfo.seatsLicensed || 0) - subscriptionInfo.seatsAvailable,
      available: subscriptionInfo.seatsLicensed
        ? subscriptionInfo.seatsLicensed - subscriptionInfo.seatsUtilized
        : 0,
      hasData: true,
    };
  }, [isCx, userCount, totalUsersCount, subscriptionInfo]);

  const canAddMembers = useMemo(() => {
    if (isCx) {
      if (!seatData || !seatData.hasData) return true;
      return seatData.available > 0;
    }
    if (
      !subscriptionInfo ||
      subscriptionInfo.status === "free" ||
      subscriptionInfo.status === "trialing" ||
      !subscriptionInfo.seatsLicensed
    ) {
      return true;
    }
    return subscriptionInfo.seatsLicensed - subscriptionInfo.seatsUtilized > 0;
  }, [isCx, seatData, subscriptionInfo]);

  const isAddMembersButtonDisabled =
    isCx && (isSubscriptionCanceled || hasUserAddonPendingInvoice);

  return (
    <>
      <div className="flex h-[72px] items-center justify-between gap-4 bg-[#EEF2F5] px-8">
        <div className="[&_input]:w-[280px]">
          <ExpandableSearchBar
            color="white"
            isAlwaysExpanded
            size="lg"
            onSearch={setSearchQuery}
            {...inputProps}
          />
        </div>
        <div className="flex gap-4">
          {seatData?.hasData && (
            <div
              className={cx(
                "shrink-0 rounded-xl bg-gradient-to-r from-[#A9BDFF] to-[#F586FF] p-px",
                isCx && "cursor-pointer"
              )}
              onClick={isCx ? () => setIsOutOfSeatsDialogOpen(true) : undefined}
            >
              <div className="flex h-[38px] items-center gap-2.5 rounded-[11px] bg-white px-6">
                <PeopleIcon className="shrink-0" />
                <span className="font-secondary text-sm text-dark">
                  {t(
                    {
                      id: "cpanel-page.seats-used-count",
                      defaultMessage:
                        "<highlightedText>Seats:</highlightedText> {used} of {total} used",
                    },
                    {
                      used: seatData.utilized,
                      total: seatData.licensed,
                      highlightedText: (text: ReactNode) => (
                        <span className="font-semibold">{text}</span>
                      ),
                    }
                  )}
                </span>
              </div>
            </div>
          )}
          {!!selectedUsers.length && (
            <button
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border-none bg-white outline-none"
              onClick={() => setIsOpenDeleteUsersConfirmationPopup(true)}
              type="button"
            >
              <BinIcon />
            </button>
          )}
          <SpotlightTooltip
            isOpen={openedSpotlight === "people"}
            placement="bottom"
            tip={t({
              id: "cpanel-page.add-workspace-people-onboarding-hint",
              defaultMessage: "Tap here to add People to the Workspace ✨",
            })}
            showButton={false}
            renderTrigger={({ ref, props }) => (
              <div ref={ref} {...props}>
                <Menu
                  placement="bottom-end"
                  renderTrigger={({ Trigger, triggerRef }) => (
                    <Tooltip
                      delay="none"
                      className="max-w-[310px] p-4 text-center"
                      isDisabled={
                        !isCx ||
                        (!isSubscriptionCanceled && !hasUserAddonPendingInvoice)
                      }
                      renderTrigger={({
                        props: tooltipProps,
                        ref: tooltipRef,
                      }) => (
                        <Trigger
                          as={Button}
                          isDisabled={isAddMembersButtonDisabled}
                          disabled={isAddMembersButtonDisabled}
                          ref={assignRefs([triggerRef, tooltipRef])}
                          iconEnd={<AddIcon />}
                          onClick={(e) => {
                            if (isCx && !canAddMembers) {
                              e.preventDefault();
                              setIsOutOfSeatsDialogOpen(true);
                              return;
                            }
                            openSpotlight(undefined);
                          }}
                          {...tooltipProps}
                        >
                          {t({
                            id: "common.add-member",
                            defaultMessage: "Add members",
                          })}
                        </Trigger>
                      )}
                    >
                      {hasUserAddonPendingInvoice
                        ? t(
                            {
                              id: "cpanel-page.addon-invoice-pending",
                              defaultMessage:
                                "Extra Seats subscription is failed.{br}<underline>Manage Subscription</underline> to add members",
                            },
                            {
                              br: <br />,
                              underline: (text: ReactNode) => (
                                <span className="underline">{text}</span>
                              ),
                            }
                          )
                        : t(
                            {
                              id: "cpanel-page.manage-canceled-subscription",
                              defaultMessage:
                                "Subscription is Canceled.{br}<underline>Manage Subscription</underline> to add members",
                            },
                            {
                              br: <br />,
                              underline: (text: ReactNode) => (
                                <span className="underline">{text}</span>
                              ),
                            }
                          )}
                    </Tooltip>
                  )}
                  onSelect={(_item, _event, close) => close()}
                  sections={[
                    [
                      {
                        id: "new-chat",
                        label: t({
                          id: "cpanel-page.add-new-member",
                          defaultMessage: "Add new member",
                        }),
                        onSelect: () => setOpenedSideBar({ sideBar: "invite" }),
                        icon: <AddMemberIcon />,
                      },
                    ],
                    [
                      {
                        id: "new-chat",
                        label: t({
                          id: "cpanel-page.copy-invitation-link",
                          defaultMessage: "Copy Invitation Link",
                        }),
                        onSelect: () =>
                          setOpenedAlert({ alert: "copyInvitation" }),
                        icon: <CopyLinkIcon />,
                      },
                    ],
                  ]}
                />
              </div>
            )}
            onDismiss={() => openSpotlight(undefined)}
          />
        </div>
      </div>
      <OutOfSeatsDialog
        isOpen={isOutOfSeatsDialogOpen}
        usedSeats={totalUsersCount}
        totalSeats={userCount}
        extraSeats={extraUserCount}
        isFreePlan={isFree}
        isShopifyBilling={isShopifyBilling}
        onRequestClose={() => setIsOutOfSeatsDialogOpen(false)}
      />
      <DeleteUsersConfirmation
        isOpen={isOpenDeleteUsersConfirmationPopup}
        onRequestClose={() => setIsOpenDeleteUsersConfirmationPopup(false)}
        users={selectedUsers}
        onSuccess={clearSelectedUsers}
      />
    </>
  );
};
